import React, { useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import { formatFileSize, formatDate, appConfig } from '@/config/app-config';
import type { S3Object, SyncErrorLog } from '@/types';

interface FileListProps {
  objects: S3Object[];
  folders: string[];
  currentPrefix: string;
  rootLabel: string;
  selectedKeys: string[];
  isLoading: boolean;
  syncErrors?: SyncErrorLog[];
  onSelectKeys: (keys: string[]) => void;
  onFolderClick: (folderPath: string) => void;
  onNavigateToRoot: () => void;
  onNavigateToBreadcrumb: (path: string) => void;
  onDelete: (keys: string[]) => void;
  onDownload: (key: string) => void;
  onPreview: (key: string) => void;
  onMove: (sourceKey: string) => void;
  onRename: (key: string) => void;
}

type SortField = 'name' | 'size' | 'lastModified';
type SortOrder = 'asc' | 'desc';

/**
 * ファイル一覧コンポーネント
 */
const FileList: React.FC<FileListProps> = ({
  objects,
  folders,
  currentPrefix,
  rootLabel,
  selectedKeys,
  isLoading,
  syncErrors = [],
  onSelectKeys,
  onFolderClick,
  onNavigateToRoot,
  onNavigateToBreadcrumb,
  onDelete,
  onDownload,
  onPreview,
  onMove,
  onRename,
}) => {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: S3Object | null;
  } | null>(null);

  // パンくずリストの生成
  const breadcrumbs = React.useMemo(() => {
    if (!currentPrefix) return [];

    const baseParts = currentPrefix.split('/').filter(Boolean);
    const isSplitEnabled = appConfig.enableFolderSplit;
    const startIndex = isSplitEnabled ? 3 : 1;

    if (baseParts.length < startIndex) return [];

    const crumbs: { label: string; path: string }[] = [];
    let path = isSplitEnabled 
      ? `${baseParts[0]}/${baseParts[1]}/${baseParts[2]}/`
      : `${baseParts[0]}/`;

    for (let i = startIndex; i < baseParts.length; i++) {
      path += `${baseParts[i]}/`;
      crumbs.push({
        label: baseParts[i],
        path,
      });
    }

    return crumbs;
  }, [currentPrefix]);

  // ソート処理
  const sortedItems = React.useMemo(() => {
    // フォルダとファイルを結合
    const folderItems: S3Object[] = folders.map(f => ({
      key: f,
      name: f.split('/').filter(Boolean).pop() || f,
      size: 0,
      lastModified: '',
      isFolder: true,
    }));

    const allItems = [...folderItems, ...objects.filter(o => !o.isFolder)];

    return allItems.sort((a, b) => {
      // フォルダを常に上に
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;

      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'lastModified':
          comparison = (a.lastModified || '').localeCompare(b.lastModified || '');
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [objects, folders, sortField, sortOrder]);


  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onSelectKeys(sortedItems.map(item => item.key));
    } else {
      onSelectKeys([]);
    }
  };

  const handleSelectItem = (key: string) => {
    const newSelected = selectedKeys.includes(key)
      ? selectedKeys.filter(k => k !== key)
      : [...selectedKeys, key];
    onSelectKeys(newSelected);
  };

  const handleContextMenu = (event: React.MouseEvent, item: S3Object) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      item,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleItemClick = (item: S3Object) => {
    if (item.isFolder) {
      onFolderClick(item.key);
    }
  };

  const handleItemDoubleClick = (item: S3Object) => {
    if (item.isFolder) {
      onFolderClick(item.key);
    } else {
      onPreview(item.key);
    }
  };

  // ファイルに関連するエラーを取得
  const getFileError = useCallback((fileKey: string): SyncErrorLog | undefined => {
    if (!syncErrors || syncErrors.length === 0) return undefined;

    // ファイルキーのパターンでマッチング
    // documentIdはS3キーまたはファイル名を含む可能性がある
    return syncErrors.find(error => {
      if (!error.documentId) return false;

      const normDocId = error.documentId.normalize('NFC');
      const normFileKey = fileKey.normalize('NFC');

      // 完全一致
      if (normDocId === normFileKey) return true;

      // S3 URIからキーを抽出してマッチング
      if (normDocId.startsWith('s3://')) {
        const s3Path = normDocId.replace(/^s3:\/\/[^/]+\//, '');
        if (s3Path === normFileKey) return true;
      }

      // ファイル名で部分マッチング
      const fileName = normFileKey.split('/').pop();
      const errorFileName = normDocId.split('/').pop();
      if (fileName && errorFileName && fileName === errorFileName) return true;

      // documentIdがfileKeyの一部を含む場合
      if (normDocId.includes(normFileKey) || normFileKey.includes(normDocId)) return true;

      return false;
    });
  }, [syncErrors]);

  // エラーメッセージを短縮
  const truncateErrorMessage = (message: string, maxLength: number = 50): string => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <Box>
      {/* パンくずリスト */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            component="button"
            variant="body2"
            onClick={onNavigateToRoot}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            underline="hover"
          >
            <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
            {rootLabel}
          </Link>
          {breadcrumbs.map((crumb, index) => (
            index === breadcrumbs.length - 1 ? (
              <Typography key={crumb.path} variant="body2" color="text.primary">
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={crumb.path}
                component="button"
                variant="body2"
                onClick={() => onNavigateToBreadcrumb(crumb.path)}
                underline="hover"
              >
                {crumb.label}
              </Link>
            )
          ))}
        </Breadcrumbs>
      </Box>

      {/* ファイル一覧テーブル */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedKeys.length > 0 && selectedKeys.length < sortedItems.length}
                  checked={sortedItems.length > 0 && selectedKeys.length === sortedItems.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  名前
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'size'}
                  direction={sortField === 'size' ? sortOrder : 'asc'}
                  onClick={() => handleSort('size')}
                >
                  サイズ
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'lastModified'}
                  direction={sortField === 'lastModified' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lastModified')}
                >
                  更新日時
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" width={50}>
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : sortedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    オブジェクトがありません
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedItems.map((item) => {
                const fileError = !item.isFolder ? getFileError(item.key) : undefined;
                return (
                  <TableRow
                    key={item.key}
                    hover
                    selected={selectedKeys.includes(item.key)}
                    onClick={() => handleItemClick(item)}
                    onDoubleClick={() => handleItemDoubleClick(item)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: fileError ? 'error.50' : 'inherit',
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedKeys.includes(item.key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleSelectItem(item.key)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.isFolder ? (
                          <FolderIcon color="action" />
                        ) : (
                          <InsertDriveFileIcon color="action" />
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">{item.name}</Typography>
                            {fileError && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="caption" fontWeight="bold">
                                      同期エラー
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      {fileError.message}
                                    </Typography>
                                    {fileError.errorCode && (
                                      <Typography variant="caption" color="error.light" display="block">
                                        コード: {fileError.errorCode}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              >
                                <WarningAmberIcon
                                  fontSize="small"
                                  color="error"
                                  sx={{ cursor: 'help' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          {fileError && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {truncateErrorMessage(fileError.message)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {item.isFolder ? '-' : formatFileSize(item.size)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.lastModified ? formatDate(item.lastModified) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="メニュー">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContextMenu(e, item);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>


      {/* コンテキストメニュー */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.item && (
          <>
            {/* ファイルのみ利用可能なメニュー */}
            {!contextMenu.item.isFolder && (
              <>
                <MenuItem onClick={() => {
                  onPreview(contextMenu.item!.key);
                  handleCloseContextMenu();
                }}>
                  <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>プレビュー</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                  onDownload(contextMenu.item!.key);
                  handleCloseContextMenu();
                }}>
                  <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>ダウンロード</ListItemText>
                </MenuItem>
              </>
            )}

            {/* ファイル・フォルダ共通メニュー */}
            <MenuItem onClick={() => {
              onMove(contextMenu.item!.key);
              handleCloseContextMenu();
            }}>
              <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
              <ListItemText>移動</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => {
              onRename(contextMenu.item!.key);
              handleCloseContextMenu();
            }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>名前変更</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => {
              onDelete([contextMenu.item!.key]);
              handleCloseContextMenu();
            }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>削除</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default FileList;
